<script setup lang="ts">
import { ref, computed, watch, onUnmounted, nextTick } from 'vue';
import { X, ExternalLink } from 'lucide-vue-next';
import OTPFormField from '../ui/special/OTPFormField.vue';
import Button from '../../button/Button.vue';
import type { FormField } from '../types/formTypeStructure';
import type { OTPServerResponse } from '../hooks/useOTPHandler';
import { getLocalizedText } from '../utils/formHelpers';

interface Props {
  isOpen: boolean;
  field: FormField;
  currentLang: string;
  formValues: Record<string, any>;
  serverResponse: OTPServerResponse | null;
  isSending: boolean;
  isResending?: boolean;
  resendCooldown?: number;
  otpValidated?: boolean;
  onClose: () => void;
  onSendOTP: () => void;
  onValidateOTP: () => void;
  onResendOTP?: () => void;
  onCallbackRedirect: (url: string) => void;
  onSubmit?: () => void;
  onNext?: () => void;
  isLastSection?: boolean;
  linkedEmail?: string | null;
  onOTPComplete?: (otpCode: string) => void;
}

const props = withDefaults(defineProps<Props>(), {
  isResending: false,
  resendCooldown: 0,
  otpValidated: false,
  isLastSection: false,
  linkedEmail: null,
});

const modalRef = ref<HTMLDivElement | null>(null);
const overlayRef = ref<HTMLDivElement | null>(null);
const contentRef = ref<HTMLDivElement | null>(null);

// Animation d'ouverture
watch(() => props.isOpen, async (isOpen) => {
  if (isOpen) {
    await nextTick();
    
    setTimeout(() => {
      if (modalRef.value && overlayRef.value && contentRef.value) {
        void modalRef.value.offsetHeight;
        
        setTimeout(() => {
          if (overlayRef.value) {
            overlayRef.value.style.opacity = '1';
            overlayRef.value.style.backdropFilter = 'blur(8px)';
            overlayRef.value.style.webkitBackdropFilter = 'blur(8px)';
          }
          if (contentRef.value) {
            contentRef.value.style.transform = 'scale(1) translateY(0)';
            contentRef.value.style.opacity = '1';
          }
        }, 10);
      }
    }, 50);
  } else if (contentRef.value && overlayRef.value) {
    contentRef.value.style.transform = 'scale(0.9) translateY(20px)';
    contentRef.value.style.opacity = '0';
    overlayRef.value.style.opacity = '0';
    overlayRef.value.style.backdropFilter = 'blur(0px)';
    overlayRef.value.style.webkitBackdropFilter = 'blur(0px)';
  }
}, { immediate: true });

// Bloquer le scroll
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});

onUnmounted(() => {
  document.body.style.overflow = '';
});

const hasCallbackUrl = computed(() => 
  props.serverResponse?.call_back_url && props.serverResponse.call_back_url.trim() !== ''
);

const fieldLabel = computed(() => 
  getLocalizedText(props.field.label, props.currentLang)
);

const helpText = computed(() => {
  if (!props.field.helpText) return null;
  return getLocalizedText(props.field.helpText, props.currentLang);
});

const emit = defineEmits<{
  close: [];
  sendOTP: [];
  validateOTP: [];
  resendOTP: [];
  callbackRedirect: [url: string];
  submit: [];
  next: [];
  otpComplete: [otpCode: string];
}>();

const handleClose = () => {
  emit('close');
};

const handleValidateOTP = () => {
  emit('validateOTP');
};

const handleResendOTP = () => {
  if (props.onResendOTP) {
    emit('resendOTP');
  }
};

const handleCallbackRedirect = (url: string) => {
  emit('callbackRedirect', url);
};

const handleSubmit = () => {
  if (props.onSubmit) {
    emit('submit');
  }
};

const handleNext = () => {
  if (props.onNext) {
    emit('next');
  }
};

const handleOTPComplete = (otpCode: string) => {
  if (props.onOTPComplete) {
    emit('otpComplete', otpCode);
  }
};
</script>

<template>
  <Teleport to="body">
    <div
      v-show="isOpen"
      ref="modalRef"
      class="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4"
      :style="{ display: isOpen ? 'flex' : 'none' }"
    >
      <!-- FULLSCREEN OVERLAY -->
      <div
        ref="overlayRef"
        class="fixed inset-0 bg-black/50 transition-all duration-300"
        style="opacity: 0; backdrop-filter: blur(0px); -webkit-backdrop-filter: blur(0px);"
        @click="handleClose"
      />

      <!-- MODAL CONTENT -->
      <div
        ref="contentRef"
        class="relative z-10 bg-gradient-to-br from-gray-50 to-gray-100 w-[95%] sm:w-[500px] max-w-[600px] max-h-[95vh] sm:max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col transition-all duration-200"
        style="transform: scale(0.9) translateY(20px); opacity: 0;"
      >
        <!-- CLOSE BUTTON - Top Right -->
        <button
          type="button"
          @click="handleClose"
          class="absolute top-4 right-4 z-20 text-gray-400 hover:text-gray-700 transition-colors bg-white/80 hover:bg-white rounded-full p-2 shadow-sm"
        >
          <X :size="20" />
        </button>

        <!-- MAIN CONTENT -->
        <div class="flex-1 overflow-y-auto px-6 sm:px-12 py-8 sm:py-12 custom-scrollbar">
          <!-- ICON -->
          <div class="flex justify-center mb-6">
            <div class="w-24 h-24 sm:w-28 sm:h-28 bg-[#004976] rounded-full flex items-center justify-center shadow-lg">
              <svg class="w-12 h-12 sm:w-14 sm:h-14 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <!-- Face scan icon -->
                <path d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="9" cy="10" r="1" fill="currentColor"/>
                <circle cx="15" cy="10" r="1" fill="currentColor"/>
                <path d="M9 14s1 2 3 2 3-2 3-2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

          <!-- TITLE -->
          <h2 class="text-center text-2xl sm:text-3xl font-bold text-[#004976] mb-3 tracking-tight">
            {{ fieldLabel || "CONFIRMATION DE L'OSC" }}
          </h2>

          <!-- SUBTITLE -->
          <p class="text-center text-sm sm:text-base text-gray-700 mb-8 px-2 leading-relaxed">
            {{ helpText || "Un code à 6 chiffre vous a été envoyer par mail veuillez renseigner pour confirmer votre identité." }}
          </p>

          <!-- CODE OTP LABEL -->
          <div class="mb-4">
            <label class="block text-left font-semibold text-gray-900 text-sm sm:text-base">
              Code OTP
            </label>
          </div>

          <!-- OTP INPUT FIELD -->
          <div class="mb-6">
            <OTPFormField
              :field="field as FormField & { fieldType: 'OTP' }"
              :current-lang="currentLang"
              :on-otp-complete="handleOTPComplete"
            />
          </div>

          <!-- VALIDATE BUTTON -->
          <div class="mb-6">
            <button
              type="button"
              :disabled="isSending || otpValidated"
              @click="handleValidateOTP"
              class="w-full bg-[#004976] hover:bg-[#003558] text-white font-semibold py-3.5 sm:py-4 px-6 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg"
            >
              {{ isSending ? 'Validation en cours...' : otpValidated ? 'Code validé ✓' : 'Valider' }}
            </button>
          </div>

          <!-- RESEND CODE LINK -->
          <div
            v-if="field.otpFormFieldExecOptions?.enableResend !== false && !otpValidated && onResendOTP"
            class="text-center"
          >
            <p class="text-sm text-gray-700 inline">
              Vous n'avez pas reçu de code ?
            </p>
            <button
              type="button"
              :disabled="isResending || resendCooldown > 0"
              @click="handleResendOTP"
              class="text-sm text-teal-600 hover:text-teal-800 font-medium disabled:text-gray-400 disabled:cursor-not-allowed transition-colors ml-1"
            >
              <span v-if="isResending">Envoi en cours...</span>
              <span v-else-if="resendCooldown > 0">Renvoyer ({{ resendCooldown }}s)</span>
              <span v-else class="underline">Renvoyer le code</span>
            </button>
          </div>

          <!-- Afficher l'email lié si disponible -->
          <div v-if="linkedEmail" class="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p class="text-sm text-blue-800 text-center">
              Code envoyé à : <span class="font-semibold">{{ linkedEmail }}</span>
            </p>
          </div>

          <!-- Affichage des messages serveur -->
          <div
            v-if="serverResponse?.message"
            :class="[
              'mt-6 p-3 rounded-lg text-center',
              serverResponse.success
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            ]"
          >
            <p class="text-sm">{{ serverResponse.message }}</p>
          </div>

          <!-- Lien de redirection si call_back_url présent -->
          <div v-if="hasCallbackUrl" class="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p class="text-sm text-blue-800 mb-3 text-center font-medium">
              Continuer sur :
            </p>
            <button
              type="button"
              @click="handleCallbackRedirect(serverResponse!.call_back_url!)"
              class="flex items-center justify-center gap-2 text-blue-600 hover:text-blue-800 font-medium transition-colors w-full"
            >
              <ExternalLink :size="16" />
              <span class="underline break-all text-sm">{{ serverResponse!.call_back_url }}</span>
            </button>
          </div>

          <!-- Action buttons après validation -->
          <div v-if="!hasCallbackUrl && otpValidated" class="mt-6">
            <button
              type="button"
              @click="isLastSection && onSubmit ? handleSubmit() : handleNext()"
              class="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3.5 px-6 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
            >
              {{ isLastSection ? 'Soumettre le formulaire' : 'Continuer' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>